import {useMutation} from "@apollo/client";
import toast from "react-hot-toast";

import {Alert} from "@/components/alert/Alert.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {
  GQL_END_ACTIVE_MAINTENANCE_WINDOW,
  GQL_MAINTENANCE_OCCURRENCES_LIST,
  GQL_MAINTENANCE_SCHEDULES_LIST,
} from "@/lib/queries/maintenance.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {MaintenanceOccurrence, MaintenanceScheduleType} from "@/lib/types/maintenance.ts";

export interface EndActiveWindowDialogProps {
  occurrence: MaintenanceOccurrence;
  show: boolean;
  onClose: () => void;
}

export const EndActiveWindowDialog = ({occurrence, show, onClose}: EndActiveWindowDialogProps) => {
  const [endActiveWindow, {loading}] = useMutation(GQL_END_ACTIVE_MAINTENANCE_WINDOW, {
    refetchQueries: [GQL_MAINTENANCE_OCCURRENCES_LIST, GQL_MAINTENANCE_SCHEDULES_LIST],
  });

  const handleConfirm = async () => {
    const response = await endActiveWindow({
      variables: {occurrenceId: occurrence.id},
    });
    if (response.data?.response?.status === ResponseStatus.SUCCESS) {
      onClose();
      toast.success("Active Maintenance Window ended successfully.");
    } else {
      toast.error(response.data?.response?.message || "Failed to end active Maintenance Window.");
    }
  };

  return (
    <ActionDialog
      show={show}
      title="End Active Window"
      confirmBtnText="Confirm"
      loading={loading}
      onCancel={onClose}
      onConfirm={handleConfirm}
    >
      <div>
        <p>
          Are you sure you want to end this active Maintenance Window?{" "}
          <span className={"pulseTextBodyBold"}>{occurrence.schedule.name}</span> will end immediately and monitoring
          will resume for all linked Checks.
        </p>
        {occurrence.schedule.schedule_type === MaintenanceScheduleType.RRULE && (
          <Alert variant="maintenance" callout="Attention">
            This will end the current active cycle. Any recurring schedules will run as configured.
          </Alert>
        )}
      </div>
    </ActionDialog>
  );
};
